import { plainHeadingText } from '../fmHostedHeading';

type CopyRecord = Record<string, unknown>;

/** One printed line of a section: where it sits, the list it belongs to, and its text. */
interface CopyLine {
  path: string;
  /** Items of one list (every fact's detail, every figure's footnote) share a group. */
  group: string;
  text: unknown;
}

function record(value: unknown): CopyRecord | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as CopyRecord)
    : undefined;
}

function list(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function fields(element: CopyRecord, base: string, keys: readonly string[]): CopyLine[] {
  return keys.map((key) => ({
    path: `${base}.${key}`,
    group: `${base}.${key}`,
    text: element[key],
  }));
}

function listField(items: unknown, base: string, field: string, key: string): CopyLine[] {
  return list(items).map((item, index) => ({
    path: `${base}.${field}[${index}]${key ? `.${key}` : ''}`,
    group: `${base}.${field}.${key}`,
    text: key ? record(item)?.[key] : item,
  }));
}

// Sources form one list across the section: two charts or maps may cite the
// same dataset, but a source line never repeats a caption or heading.
function sourceLabel(base: string, source: unknown): CopyLine {
  return { path: `${base}.source.label`, group: 'source', text: record(source)?.label };
}

function mediaLines(element: CopyRecord, base: string): CopyLine[] {
  const slotProps = record(record(element.figure)?.slotProps);
  return [
    ...fields(element, base, ['title', 'caption', 'takeaway', 'interactionHint']),
    { path: `${base}.figure.slotProps.caption`, group: `${base}.figure`, text: slotProps?.caption },
    sourceLabel(base, element.source),
  ];
}

/**
 * The lines an element prints as prose. Values, labels, button text and
 * unavailable reasons are left out: a label or a short reason legitimately
 * recurs, a sentence never should.
 */
function elementLines(element: CopyRecord, base: string): CopyLine[] {
  switch (element.element) {
    case 'copy':
      return [
        ...fields(element, base, ['eyebrow', 'heading', 'lede', 'sourceNote']),
        ...listField(element.body, base, 'body', ''),
        ...listField(element.list, base, 'list', ''),
      ];
    case 'keyFigures':
      return listField(element.figures, base, 'figures', 'footnote');
    case 'facts':
      return listField(element.items, base, 'items', 'detail');
    case 'definitionList':
      return listField(element.items, base, 'items', 'definition');
    case 'chart':
      return [
        ...fields(element, base, [
          'title',
          'caption',
          'takeaway',
          'explanationTitle',
          'contextDetail',
        ]),
        sourceLabel(base, element.source),
      ];
    case 'map':
    case 'image':
      return mediaLines(element, base);
    case 'table': {
      const table = record(element.table) ?? record(element.rankedTable);
      return [
        ...fields(element, base, ['title']),
        ...(table ? fields(table, `${base}.table`, ['title', 'caption', 'summary']) : []),
      ];
    }
    case 'rankedList':
      return fields(element, base, ['title']);
    case 'notice':
      return fields(element, base, ['title', 'description']);
    default:
      return [];
  }
}

function slotLines(slots: unknown, base: string): CopyLine[] {
  return Object.entries(record(slots) ?? {}).flatMap(([slot, elements]) =>
    list(elements).flatMap((element, index) => {
      const value = record(element);
      return value ? elementLines(value, `${base}.slots.${slot}[${index}]`) : [];
    }),
  );
}

function rejectRepeats(lines: readonly CopyLine[]): void {
  const seen = new Map<string, CopyLine>();
  lines.forEach((line) => {
    const key = plainHeadingText(typeof line.text === 'string' ? line.text : null);
    if (key === null) return;
    const first = seen.get(key);
    if (first && first.group !== line.group) {
      throw new Error(
        `Invalid descriptor: ${line.path} repeats ${first.path} ("${String(line.text)}"); a section prints each sentence once.`,
      );
    }
    if (!first) seen.set(key, line);
  });
}

/**
 * Every line a section prints (copy, captions, takeaways, sources, footnotes,
 * notices) says something different. Repeating one string in two slots prints
 * the same sentence twice on screen. A tab or slide is checked together with
 * the section's shared slots and notices, because only one panel shows at a
 * time.
 */
export function rejectRepeatedSectionCopy(section: CopyRecord, path: string): void {
  const shared = [
    ...slotLines(section.slots, path),
    ...list(section.notices).flatMap((notice, index) => {
      const value = record(notice);
      // Notices form one list: distinct served codes may share a generic title
      // (each still says something different in its description).
      return value
        ? elementLines(value, `${path}.notices[${index}]`).map((line) => ({
            ...line,
            group: `${path}.notices.${line.path.split('.').pop() ?? ''}`,
          }))
        : [];
    }),
  ];
  const panels = [...list(section.panels), ...list(section.slides)];
  if (panels.length === 0) {
    rejectRepeats(shared);
    return;
  }
  const panelKey = Array.isArray(section.panels) ? 'panels' : 'slides';
  panels.forEach((panel, panelIndex) => {
    const elements = list(record(panel)?.elements);
    rejectRepeats([
      ...shared,
      ...elements.flatMap((element, index) => {
        const value = record(element);
        return value
          ? elementLines(value, `${path}.${panelKey}[${panelIndex}].elements[${index}]`)
          : [];
      }),
    ]);
  });
}
